import { useEffect, useMemo, useState } from "react";
import { Download, Search, Sparkles } from "lucide-react";
import { useLocation } from "react-router-dom";

import { api, apiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { downloadQuotePdf } from "@/lib/quotePdf";
import ComparisonPanel from "@/components/ComparisonPanel";

const monthsOptions = [12, 24, 36, 48, 60, 72, 84, 96, 120];
const quickPercents = [5, 10, 15, 20, 30, 50, 100];
const getPaltasData = (quote) => {
  if (!quote?.plan_paltas) return null;

  const planInicial = quote.plan_inicial || {};
  const planPagos = quote.plan_pagos || [];

  const mesesInicial = Number(
  quote.duracion_inicial_meses ??
  planInicial.meses ??
  planInicial.meses_inicial ??
  12
);

  const montoInicial = Number(
    planInicial.monto_total ||
    planInicial.total ||
    quote.inicial ||
    10500
  );

  const cuotaInicial = Number(
    planInicial.cuota_mensual ||
    planInicial.valor_cuota ||
    (mesesInicial > 0 ? montoInicial / mesesInicial : 0)
  );

  const mesesSaldo = Number(
  quote.duracion_saldo_meses ??
  quote.saldo_meses ??
  quote.meses_saldo ??
  60
);

  const saldo = Number(
    quote.saldo ||
    0
  );

  const cuotaSaldo = Number(
    quote.valor_cuota ||
    (mesesSaldo > 0 ? saldo / mesesSaldo : 0)
  );

  const precioReal = Number(
    quote.precio_real ||
    quote.precio_original ||
    quote.precio_total_real ||
    quote.precio_total ||
    0
  );

  const precioPaltas = Number(
    quote.precio_base_paltas ||
    quote.precio_objetivo ||
    quote.precio_comercial ||
    35000
  );

  return {
    mesesInicial,
    montoInicial,
    cuotaInicial,
    mesesSaldo,
    saldo,
    cuotaSaldo,
    precioReal,
    precioPaltas,
    planPagos,
  };
};

export default function QuotePage() {
  const { formatAmount, settings } = useCurrency();
  const location = useLocation();
  const money = formatAmount;
  const [manzano, setManzano] = useState("");
  const [lote, setLote] = useState("");
  const [clients, setClients] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [lot, setLot] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState("percent");
  const [percent, setPercent] = useState(10);
  const [amount, setAmount] = useState("");
  const [months, setMonths] = useState(84);
  const [initialMonths, setInitialMonths] = useState(12);
  const [customMonths, setCustomMonths] = useState("");
  const [observations, setObservations] = useState("");
  const [quote, setQuote] = useState(null);
  const [message, setMessage] = useState("");

  const [planType, setPlanType] = useState("normal");
const [promotions, setPromotions] = useState([]);
const [cantidadLotes, setCantidadLotes] = useState(1);

  const selectedMonths = Number(months === "custom" ? customMonths : months);
  const invalidCustomMonths = months === "custom" && (
    !Number.isInteger(selectedMonths) || selectedMonths < 1 || selectedMonths > 120
  );

  const paltas = getPaltasData(quote);

  const paltasPromotion = useMemo(() => {
  if (!lot || !Array.isArray(promotions)) {
    return null;
  }

  const precio = Number(lot.precio_total || 0);

  return (
    promotions.find(
      (promotion) =>
        promotion.tipo === "paltas" &&
        promotion.activo === true &&
        precio >= Number(promotion.precio_minimo ?? 25000) &&
        precio <= Number(promotion.precio_maximo ?? 40000)
    ) || null
  );
}, [lot, promotions]);

  useEffect(() => {
    if (planType === "paltas" && lot && !paltasPromotion) {
      setPlanType("normal");
      setMode("percent");
      setPercent(10);
      setAmount("");
      setMonths(84);
      setInitialMonths(12);
      setCustomMonths("");
      setQuote(null);
      setMessage(
        "El nuevo lote no aplica al Plan Paltas. Se cambió a Plan normal."
      );
    }
  }, [planType, lot, paltasPromotion]);

  useEffect(() => {
    api.get("/clients").then((response) => setClients(response.data));
  }, []);

useEffect(() => {
  api.get("/promotions")
    .then((response) => {
      setPromotions(response.data || []);
    })
    .catch(() => {
      setPromotions([]);
    });
}, []);

  useEffect(() => {
    const planLot = location.state?.lot;
    if (!planLot) return;
    setManzano(planLot.manzano);
    setLote(planLot.lote);
    setLot(planLot);
    setAlternatives([]);
    setMessage("");
  }, [location.state]);

  useEffect(() => {
    const query = manzano || lote;
    if (!query) {
      setSuggestions([]);
      return;
    }
    api.get("/lots/suggestions", { params: { q: query } }).then((response) => {
      setSuggestions(response.data);
    });
  }, [manzano, lote]);

  const payload = useMemo(() => {
  if (!lot || !clientId || invalidCustomMonths) {
    return null;
  }

  const draft = {
    client_id: clientId,
    lot_id: lot.id,
    months: selectedMonths,
    observations,
  };

  if (planType === "paltas") {
  if (!paltasPromotion) {
    return null;
  }

  draft.promocion_id = paltasPromotion.id;

  draft.cantidad_lotes = cantidadLotes;

  draft.initial_amount = Number(
    paltasPromotion.inicial_fija
  );

  draft.initial_months = initialMonths;

  draft.months = selectedMonths;
} else {
    draft.promocion_id = null;
    draft.cantidad_lotes = 1;

    draft[
      mode === "percent"
        ? "initial_percent"
        : "initial_amount"
    ] = Number(
      mode === "percent"
        ? percent
        : amount
    );
  }

  return draft;
}, [
  amount,
  cantidadLotes,
  clientId,
  initialMonths,
  invalidCustomMonths,
  lot,
  mode,
  observations,
  paltasPromotion,
  percent,
  planType,
  selectedMonths,
]);

  const searchLot = async (event) => {
  event.preventDefault();

  setMessage("");
  setQuote(null);

  try {
    const response = await api.get("/lots/search", {
      params: { manzano, lote },
    });

    setLot(response.data.lot);
    setAlternatives(response.data.suggestions);

    // Cada lote nuevo comienza en Plan normal.
    setPlanType("normal");
    setMode("percent");
    setPercent(10);
    setAmount("");
    setMonths(84);
    setInitialMonths(12);
    setCustomMonths("");
    setCantidadLotes(1);
  } catch (error) {
    setLot(null);
    setAlternatives([]);
    setMessage(apiError(error));
  }
};

  const selectSuggestion = (item) => {
  setManzano(item.manzano);
  setLote(item.lote);
  setSuggestions([]);

  setPlanType("normal");
  setMode("percent");
  setPercent(10);
  setAmount("");
  setMonths(84);
  setCustomMonths("");
  setCantidadLotes(1);
  setQuote(null);
  setMessage("");
};

 const preview = async () => {
  if (planType === "paltas" && !paltasPromotion) {
    setMessage(
      "Este lote no está dentro del rango permitido para el Plan Paltas."
    );
    return;
  }

  if (invalidCustomMonths) {
      setMessage("El plazo personalizado debe estar entre 1 y 120 meses.");
      return;
    }
    if (!payload) {
      setMessage("Seleccione un cliente para calcular el plan.");
      return;
    }
    try {
      const response = await api.post("/quotes/preview", payload);
      setQuote(response.data);
      setMessage("");
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const saveAndDownload = async () => {
    if (planType === "paltas" && !paltasPromotion) {
      setMessage(
        "Este lote no está dentro del rango permitido para el Plan Paltas."
      );
      return;
    }
    if (invalidCustomMonths) {
      setMessage("El plazo personalizado debe estar entre 1 y 120 meses.");
      return;
    }
    if (!payload) {
      setMessage("Seleccione un cliente para guardar la cotización.");
      return;
    }
    try {
      const response = await api.post("/quotes", payload);
      setQuote(response.data);
      await downloadQuotePdf(response.data, settings);
      setMessage("Cotización guardada. El PDF se descargó correctamente.");
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  const applyAlternative = async (option) => {
  if (planType !== "normal") return;
  if (!lot || !clientId) return;
    setMode("percent");
    setPercent(option.inicial_porcentaje);
    setMonths(option.meses || 12);
    setCustomMonths("");
    try {
      const response = await api.post("/quotes/preview", {
        client_id: clientId,
        lot_id: lot.id,
        initial_percent: option.inicial_porcentaje,
        months: option.meses || 12,
        observations,
      });
      setQuote(response.data);
      setMessage("Alternativa aplicada a la cotización actual.");
    } catch (error) {
      setMessage(apiError(error));
    }
  };

  return (
    <section className="page quote-page" data-testid="quote-page">
      <header className="page-header compact">
  <div>
    <span className="eyebrow">HERRAMIENTA COMERCIAL</span>

    <h1>Nueva cotización</h1>

    <p>
      Encuentra un lote, configura las condiciones y genera
      una propuesta comercial profesional.
    </p>
  </div>

  <div className="quote-header-status">
    <span className="quote-header-status-dot"></span>
    NUEVA PROPUESTA
  </div>
</header>
      <div className="quote-layout">
        <div className="quote-editor">
          <section className="quote-section" data-testid="lot-search-section">
            <div className="section-number">01</div>
            <div><span className="eyebrow">SELECCIÓN</span><h2>Ubica el lote</h2></div>
            <form className="lot-search" data-testid="lot-search-form" onSubmit={searchLot}>
              <label htmlFor="manzano-input">Manzano</label>
              <input data-testid="manzano-input" id="manzano-input" onChange={(e) => setManzano(e.target.value)} required value={manzano} />
              <label htmlFor="lote-input">Lote</label>
              <input data-testid="lote-input" id="lote-input" onChange={(e) => setLote(e.target.value)} required value={lote} />
              <button className="dark-button" data-testid="search-lot-button" type="submit"><Search size={17} /> Buscar</button>
            </form>
            {suggestions.length > 0 && <div className="suggestion-list" data-testid="lot-suggestions-list">
              {suggestions.map((item) => <button className="suggestion" data-testid={`lot-suggestion-${item.id}`} key={item.id} onClick={() => selectSuggestion(item)} type="button">Manzano {item.manzano} · Lote {item.lote}<small>{item.estado}</small></button>)}
            </div>}
            {lot && <div className="lot-result" data-testid="lot-search-result">
              <span className={`status-tag ${lot.estado.toLowerCase()}`}>{lot.estado}</span>
              <strong>Manzano {lot.manzano} · Lote {lot.lote}</strong>
              <span>{lot.superficie} m² · {money(lot.precio_m2)} / m²</span>
              <b>{money(lot.precio_total)}</b>
            </div>}
            {alternatives.length > 0 && <div className="alternatives" data-testid="available-alternatives"><Sparkles size={16} /> Este lote fue vendido. Disponibles: {alternatives.map((item) => `${item.manzano}-${item.lote}`).join(", ")}</div>}
          </section>

          {lot && <section className="quote-section" data-testid="payment-simulator-section">
            <div className="section-number">02</div>
            <div><span className="eyebrow">CONFIGURACIÓN</span><h2>Simula el pago</h2></div>
            <div className="simulator-form">

  <label htmlFor="plan-type">
    Tipo de plan
  </label>

  <select
    id="plan-type"
    data-testid="plan-type-select"
    value={planType}
    onChange={(e) => {
      const value = e.target.value;

      setPlanType(value);
      setQuote(null);
      setMessage("");

      if (value === "paltas") {
  setMode("amount");

  setAmount(
    String(
      paltasPromotion?.inicial_fija ?? 10500
    )
  );

  // Inicial: por defecto 12 meses.
  // El usuario podrá cambiarla a contado o 1-12 meses.
  setInitialMonths(12);

  // Saldo: por defecto 60 meses.
  // El usuario podrá elegir posteriormente 12/24/36/48/60.
  setMonths(
    Number(
      paltasPromotion?.saldo_meses ?? 60
    )
  );

  setCustomMonths("");
} else {
  setMode("percent");
  setPercent(10);
  setAmount("");
  setMonths(84);
  setCustomMonths("");
}
    }}
  >
    <option value="normal">
  Plan normal
</option>

<option
  value="paltas"
  disabled={!paltasPromotion}
>
  {paltasPromotion
    ? "Plan Paltas"
    : "Plan Paltas — no disponible para este lote"}
</option>
  </select>

  <label htmlFor="quote-client">
    Cliente
  </label>
              <select data-testid="quote-client-select" id="quote-client" onChange={(e) => setClientId(e.target.value)} value={clientId}>
                <option value="">Selecciona un cliente</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {`${client.nombre} · ${client.ci}`}
                  </option>
                ))}
              </select>
{planType === "paltas" && (
  <div className="paltas-active-banner">

    <strong>🌴 PLAN PALTAS</strong>

    {paltasPromotion ? (
      <>
        <span>
          Precio permitido:{" "}
          {money(paltasPromotion.precio_minimo)}
          {" "}–{" "}
          {money(paltasPromotion.precio_maximo)}
        </span>

        <span>
  Inicial:{" "}
  {money(paltasPromotion.inicial_fija)}
  {" "}·{" "}
  {initialMonths === 0
    ? "al contado"
    : `${initialMonths} meses`}
</span>

<span>
  Saldo:{" "}
  {months} meses
</span>
      </>
    ) : (
      <span>
        Este lote no aplica al Plan Paltas.
      </span>
    )}

  </div>
)}

              {planType === "paltas" ? (
  <>
    <div className="paltas-initial-box">
      <label>
        Inicial Plan Paltas
      </label>

      <strong>
        {paltasPromotion
          ? money(paltasPromotion.inicial_fija)
          : "$10.500"}
      </strong>

      <span>
        {initialMonths === 0
          ? "Pago de la inicial al contado"
          : `Prorrateada en ${initialMonths} meses`}
      </span>

      <small>
        {initialMonths === 0
          ? "La inicial se paga completamente al inicio."
          : `Durante ${initialMonths} meses se paga únicamente la inicial.`}
      </small>
    </div>

    <label htmlFor="paltas-initial-months">
      Forma de pago de la inicial
    </label>

    <select
      id="paltas-initial-months"
      data-testid="paltas-initial-months-select"
      value={initialMonths}
      onChange={(e) => {
        setInitialMonths(Number(e.target.value));
        setQuote(null);
      }}
    >
      <option value={0}>
        Al contado
      </option>

      <option value={1}>
        1 mes
      </option>

      <option value={2}>
        2 meses
      </option>

      <option value={3}>
        3 meses
      </option>

      <option value={4}>
        4 meses
      </option>

      <option value={5}>
        5 meses
      </option>

      <option value={6}>
        6 meses
      </option>

      <option value={7}>
        7 meses
      </option>

      <option value={8}>
        8 meses
      </option>

      <option value={9}>
        9 meses
      </option>

      <option value={10}>
        10 meses
      </option>

      <option value={11}>
        11 meses
      </option>

      <option value={12}>
        12 meses
      </option>
    </select>

    <label htmlFor="cantidad-lotes">
      Cantidad de lotes
    </label>

    <input
      id="cantidad-lotes"
      data-testid="cantidad-lotes-input"
      min="1"
      max="100"
      type="number"
      value={cantidadLotes}
      onChange={(e) =>
        setCantidadLotes(
          Math.max(1, Number(e.target.value || 1))
        )
      }
    />
  </>
) : (
  <>
    <div
      className="method-toggle"
      data-testid="down-payment-mode-toggle"
    >
      <button
        className={mode === "percent" ? "active" : ""}
        data-testid="percent-mode-button"
        onClick={() => setMode("percent")}
        type="button"
      >
        Porcentaje
      </button>

      <button
        className={mode === "amount" ? "active" : ""}
        data-testid="amount-mode-button"
        onClick={() => setMode("amount")}
        type="button"
      >
        Monto manual
      </button>
    </div>

    {mode === "percent" ? (
      <>
        <label htmlFor="initial-percent">
          Inicial
        </label>

        <input
          data-testid="initial-percent-input"
          id="initial-percent"
          max="100"
          min="0"
          onChange={(e) => setPercent(e.target.value)}
          type="number"
          value={percent}
        />

        <div className="quick-options">
          {quickPercents.map((value) => (
            <button
              className={
                Number(percent) === value
                  ? "selected"
                  : ""
              }
              data-testid={`quick-percent-${value}`}
              key={value}
              onClick={() => setPercent(value)}
              type="button"
            >
              {value}%
            </button>
          ))}
        </div>
      </>
    ) : (
      <>
        <label htmlFor="initial-amount">
          Monto inicial
        </label>

        <input
          data-testid="initial-amount-input"
          id="initial-amount"
          min="0"
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0"
          type="number"
          value={amount}
        />
      </>
    )}
  </>
)}

<label htmlFor="quote-months">
  Plazo del saldo
</label>

<select
  data-testid="quote-months-select"
  id="quote-months"
  onChange={(e) => setMonths(Number(e.target.value))}
  value={months}
>
  {(planType === "paltas"
    ? [12, 24, 36, 48, 60]
    : monthsOptions
  ).map((value) => (
    <option key={value} value={value}>
      {value} meses
    </option>
  ))}

  {planType !== "paltas" && (
    <option value="custom">
      Otro plazo
    </option>
  )}
</select>

{months === "custom" && planType !== "paltas" && (
  <>
    <label htmlFor="custom-months">
      Plazo personalizado
    </label>

    <input
      data-testid="custom-months-input"
      id="custom-months"
      max="120"
      min="1"
      onChange={(e) => setCustomMonths(e.target.value)}
      placeholder="Entre 1 y 120 meses"
      required
      type="number"
      value={customMonths}
    />
  </>
)}

<label htmlFor="quote-observations">
  Observaciones
</label>

<textarea
  data-testid="quote-observations-input"
  id="quote-observations"
  onChange={(e) => setObservations(e.target.value)}
  value={observations}
/>

<button
  className="outline-button"
  data-testid="quote-preview-button"
  onClick={preview}
  type="button"
>
  Actualizar simulación
</button>
                </div>
         </section>}
      </div>

      <aside className="quote-summary" data-testid="quote-summary-panel">
          <span className="eyebrow">RESUMEN DE PROPUESTA</span>
          {quote ? (
  <>
    {paltas ? (
      <>
        <div className="paltas-header">
          <span className="eyebrow">PROMOCIÓN ESPECIAL</span>
          <h2>🌴 PLAN PALTAS</h2>
          <p>
  {paltas.mesesInicial === 0
    ? "La inicial se paga al contado y el financiamiento del saldo comienza posteriormente."
    : `Durante ${paltas.mesesInicial} meses se paga únicamente la inicial. Finalizado ese período comienza el financiamiento del saldo.`}
</p>
        </div>

        <div className="paltas-price-card">
          <div>
            <span>Precio real del terreno</span>
            <strong>{money(paltas.precioReal)}</strong>
          </div>

          <div>
            <span>Valor comercial Plan Paltas</span>
            <strong>{money(paltas.precioPaltas)}</strong>
          </div>
        </div>

        <div className="paltas-stage">
          <span className="eyebrow">ETAPA 1</span>
          <h3>Inicial · {paltas.mesesInicial} meses</h3>

          <div className="paltas-stage-main">
            <span>Inicial total</span>
            <strong>{money(paltas.montoInicial)}</strong>
          </div>

          <div className="paltas-stage-payment">
            <span>Cuota mensual</span>
            <strong>{money(paltas.cuotaInicial)}</strong>
          </div>

          <p>
  {paltas.mesesInicial === 0
    ? "La inicial se paga completamente al contado."
    : `Durante los primeros ${paltas.mesesInicial} meses el cliente paga solamente la inicial.`}
</p>
        </div>

        <div className="paltas-stage">
          <span className="eyebrow">ETAPA 2</span>
          <h3>Financiamiento · {paltas.mesesSaldo} meses</h3>

          <div className="paltas-stage-main">
            <span>Saldo a financiar</span>
            <strong>{money(paltas.saldo)}</strong>
          </div>

          <div className="paltas-stage-payment">
            <span>Cuota mensual</span>
            <strong>{money(paltas.cuotaSaldo)}</strong>
          </div>

          <p>
  {paltas.mesesInicial === 0
    ? "El financiamiento del saldo comienza después del pago de la inicial."
    : `El financiamiento comienza después de completar los ${paltas.mesesInicial} meses de la inicial.`}
</p>
        </div>

        <div className="paltas-total">
          <span>Valor total aplicado al plan</span>
          <strong>{money(paltas.precioPaltas)}</strong>
        </div>

        <button
          className="primary-button"
          data-testid="save-download-quote-button"
          onClick={saveAndDownload}
          type="button"
        >
          <Download size={17} />
          Guardar y descargar PDF
        </button>
      </>
    ) : (
      <>
        <h2>
          {quote.reglas_aplicadas?.length
            ? "Reglas aplicadas"
            : "Plan estándar"}
        </h2>

        <div className="summary-main">
          <span>
            {quote.meses
              ? "Valor de cuota"
              : "Pago al contado"}
          </span>

          <strong data-testid="monthly-payment-value">
            {quote.meses
              ? money(quote.valor_cuota)
              : money(quote.inicial)}
          </strong>

          <small>
            {quote.meses
              ? `durante ${quote.meses} meses`
              : "sin cuotas pendientes"}
          </small>
        </div>

        {quote.reglas_aplicadas?.length > 0 && (
          <div
            className="applied-rules"
            data-testid="applied-commercial-rules"
          >
            {quote.reglas_aplicadas.map((rule) => (
              <span
                data-testid={`applied-rule-${rule.id}`}
                key={rule.id}
              >
                {rule.nombre} ·{" "}
                {rule.modo === "igual_inicial"
                  ? `${rule.porcentaje}% igual a la inicial, sobre saldo`
                  : `${rule.porcentaje}% sobre ${
                      rule.base === "saldo"
                        ? "saldo"
                        : "precio"
                    }`}
              </span>
            ))}
          </div>
        )}

        <dl>
          <div>
            <dt>Precio total</dt>
            <dd>{money(quote.precio_total)}</dd>
          </div>

          <div>
            <dt>Inicial · {quote.inicial_porcentaje}%</dt>
            <dd>{money(quote.inicial)}</dd>
          </div>

          <div>
            <dt>Descuento</dt>
            <dd className="discount">
              − {money(quote.descuento)}
            </dd>
          </div>

          <div>
            <dt>Saldo financiado</dt>
            <dd>{money(quote.saldo)}</dd>
          </div>
        </dl>

        <button
          className="primary-button"
          data-testid="save-download-quote-button"
          onClick={saveAndDownload}
          type="button"
        >
          <Download size={17} />
          Guardar y descargar PDF
        </button>
      </>
    )}
  </>
) : (
  <div
    className="quote-placeholder"
    data-testid="quote-empty-summary"
  >
    Selecciona el lote y actualiza la simulación para ver la propuesta.
  </div>
)}
          {message && <p className="form-note" data-testid="quote-action-message">{message}</p>}
        </aside>
      </div>
      {lot && planType === "normal" && (
  <ComparisonPanel
    clientId={clientId}
    lot={lot}
    onApply={applyAlternative}
  />
)}
      {quote &&
  Array.isArray(quote.plan_pagos) &&
  quote.plan_pagos.length > 0 && (
    <section
      className="payment-plan"
      data-testid="payment-plan-table-section"
    >
      <div className="panel-heading">
        <div>
          <span className="eyebrow">CALENDARIO</span>
          <h2>Plan de pagos</h2>
        </div>
      </div>

      <div className="data-table-wrap">
        <table data-testid="payment-plan-table">
          <thead>
            <tr>
              <th>Cuota</th>
              <th>Fecha</th>
              <th>Monto</th>
              <th>Saldo restante</th>
            </tr>
          </thead>

          <tbody>
            {quote.plan_pagos.map((payment) => (
              <tr
                data-testid={`payment-row-${payment.cuota}`}
                key={payment.cuota}
              >
                <td>{payment.cuota}</td>
                <td>{payment.fecha}</td>
                <td>{money(payment.monto)}</td>
                <td>{money(payment.saldo_restante)}</td>
              </tr>
            ))}
          </tbody>
        </table>
           </div>
    </section>
  )}

  </section>
  );
}